import { useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { PreferenceDefinition } from '@solum/experience'
import { usePreferenceController } from '@solum/ui'
import { useSolum } from './client'

const QUERY_KEY = ['user-settings'] as const

/** Console-only transport binding for a caller-scoped preference definition. */
export function useUserSettingPreference<Address, Value, Update>(
  definition: PreferenceDefinition<Address, Value, Update>,
  address: Address,
  addressKey: string,
  debounceMs: number,
) {
  const { client, isLive } = useSolum()
  const qc = useQueryClient()
  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => client.raw().rpcScalar<Record<string, unknown>>('my_settings'),
    enabled: isLive,
    staleTime: 60_000,
  })
  const stored = definition.read(query.data, address)

  const save = useMutation({
    /* One setting key is one replace-whole JSON value. Serial execution plus
       the success cache patch makes sibling leaf writes compose. */
    scope: { id: `console-user-setting:${definition.settingKey}` },
    mutationFn: async (variables: { address: Address; updates: readonly Update[] }) => {
      const latest = qc.getQueryData<Record<string, unknown>>(QUERY_KEY)
      if (!latest) throw new Error('The existing personal settings have not been read yet')
      const settingValue = definition.write(latest, variables.address, variables.updates)
      await client.raw().rpcScalar('set_user_setting', {
        p_key: definition.settingKey,
        p_value: settingValue,
      })
      return { settingValue }
    },
    onSuccess: ({ settingValue }) => {
      qc.setQueryData<Record<string, unknown>>(QUERY_KEY, (current) => ({
        ...(current ?? {}), [definition.settingKey]: settingValue,
      }))
      void qc.invalidateQueries({ queryKey: QUERY_KEY })
    },
  })

  const commit = useCallback((target: Address, updates: readonly Update[]) =>
    save.mutateAsync({ address: target, updates }).then(() => undefined), [save])
  const controller = usePreferenceController({
    address,
    addressKey,
    stored,
    // Settled-on-error is enough to render a governed fallback, but only a
    // successful read is a safe base for replacing the whole setting value.
    writable: isLive && query.isSuccess,
    debounceMs,
    reduce: definition.reduce,
    commit,
  })

  return {
    ...controller,
    settings: query.data,
    ready: !query.isLoading || query.isError,
    saving: save.isPending,
  }
}
